// ── Book Chart data model & Romans example ────────────────────────────────────

/**
 * Simplified arc relationship taxonomy (from Fuller/Piper/Schreiner).
 * Full arcing has 18 types; at book-argument scale, 8 cover 95% of cases.
 */
export type ArcRelationship =
  | 'grounds'      // G  — because/for — the reason supporting the claim
  | 'inference'    // ∴  — therefore/so — conclusion drawn from preceding
  | 'purpose'      // P  — in order that/so that — intended aim
  | 'result'       // R  — with the result that — actual outcome
  | 'progression'  // +  — then/moreover — building step toward a climax
  | 'explanation'  // =  — that is/namely — clarifies or restates
  | 'contrast'     // ≠  — but/although/yet — contrary or concessive
  | 'series'       // ·  — and/likewise — parallel, coordinate contributions

export const REL_SYMBOL: Record<ArcRelationship, string> = {
  grounds:     'G',
  inference:   '∴',
  purpose:     'P',
  result:      'R',
  progression: '+',
  explanation: '=',
  contrast:    '≠',
  series:      '·',
}

export const REL_NAME: Record<ArcRelationship, string> = {
  grounds:     'Grounds — because / for',
  inference:   'Inference — therefore / so',
  purpose:     'Purpose — in order that',
  result:      'Result — with the result that',
  progression: 'Progression — then / moreover',
  explanation: 'Explanation — that is / namely',
  contrast:    'Contrast — but / although',
  series:      'Series — and / likewise',
}

export const REL_COLOR: Record<ArcRelationship, string> = {
  grounds:     '#d24a2a',
  inference:   '#2c5aa0',
  purpose:     '#5a2ca0',
  result:      '#2ca05a',
  progression: '#a07a2c',
  explanation: '#2c8fa0',
  contrast:    '#a02c5a',
  series:      '#6b7280',
}

// ── Data interfaces ───────────────────────────────────────────────────────────

export interface BookArc {
  id: string
  relationship: ArcRelationship
  label?: string          // short description of the logical move
  fromChapter: number
  fromVerse: number       // approximate verse position (anchor point)
  toChapter: number
  toVerse: number
  accent?: 'primary' | 'secondary'
}

export interface BookChapter {
  number: number
  verseCount: number
  title: string           // 4–6 word heading
  theme: string           // 1 sentence summary
  keyVerse?: number       // verse # of the key thesis verse (optional)
}

export interface BookChartData {
  book: string
  totalChapters: number
  classification: string
  unity: string           // the book's single controlling thesis (≤ 25 words)
  problem: string         // occasion / purpose (≤ 20 words)
  chapters: BookChapter[]
  arcs: BookArc[]
}

// ── Romans example data ───────────────────────────────────────────────────────

export const ROMANS_BOOK_DATA: BookChartData = {
  book: 'Romans',
  totalChapters: 16,
  classification: 'Epistle — Formal Theological Letter',
  unity: "The gospel reveals God's righteousness received by faith, condemning all sin and justifying all who believe, to the praise of his glory.",
  problem: 'Paul prepares the Roman church for his visit by presenting the full scope of the gospel.',

  chapters: [
    {
      number: 1, verseCount: 32,
      title: 'Greeting, Thesis, Universal Guilt',
      theme: 'Paul introduces the gospel: righteousness comes by faith; God\'s wrath is revealed against all who suppress the truth.',
      keyVerse: 16,
    },
    {
      number: 2, verseCount: 29,
      title: 'The Moral Person Also Guilty',
      theme: 'The self-righteous moralist stands equally condemned; God judges impartially by deeds, not heritage.',
    },
    {
      number: 3, verseCount: 31,
      title: 'All Under Sin; Righteousness Revealed',
      theme: 'Scripture confirms universal guilt; but now God\'s righteousness through faith in Christ has been manifested apart from the law.',
      keyVerse: 21,
    },
    {
      number: 4, verseCount: 25,
      title: 'Abraham: Faith Counted as Righteousness',
      theme: 'Abraham believed God and it was counted as righteousness — before circumcision, proving justification is by faith alone.',
      keyVerse: 3,
    },
    {
      number: 5, verseCount: 21,
      title: 'Peace, Hope, and Adam-Christ Contrast',
      theme: 'Justified by faith we have peace with God; where sin abounded through Adam, grace abounds much more through Christ.',
      keyVerse: 1,
    },
    {
      number: 6, verseCount: 23,
      title: 'Dead to Sin, Alive to God',
      theme: 'Union with Christ means death to sin\'s dominion; we are slaves of righteousness, not sin.',
    },
    {
      number: 7, verseCount: 25,
      title: "The Law's Role and the Divided Self",
      theme: 'The law is holy but exposes sin; the inner conflict between delight in God\'s law and captivity to sin reveals our need for rescue.',
    },
    {
      number: 8, verseCount: 39,
      title: 'No Condemnation; Life in the Spirit',
      theme: 'No condemnation for those in Christ; the Spirit gives life, intercedes, and guarantees future glory — nothing can separate us from God\'s love.',
      keyVerse: 1,
    },
    {
      number: 9, verseCount: 33,
      title: 'God\'s Sovereign Choice in Israel',
      theme: 'God\'s word has not failed; election is by God\'s sovereign mercy, not by human descent or effort.',
      keyVerse: 6,
    },
    {
      number: 10, verseCount: 21,
      title: 'Israel\'s Failure to Submit to God\'s Righteousness',
      theme: 'Israel pursued righteousness by law rather than faith; the word is near — whoever calls on the Lord will be saved.',
      keyVerse: 9,
    },
    {
      number: 11, verseCount: 36,
      title: 'Remnant, Grafting, and Doxology',
      theme: 'God has not rejected Israel; a remnant remains by grace; Gentile inclusion will provoke Israel to jealousy — to God be the glory.',
      keyVerse: 33,
    },
    {
      number: 12, verseCount: 21,
      title: 'Living Sacrifice and Love in Community',
      theme: 'Therefore, present your bodies as living sacrifices; love genuinely, serve zealously, overcome evil with good.',
      keyVerse: 1,
    },
    {
      number: 13, verseCount: 14,
      title: 'Governing Authorities and Love as Fulfillment',
      theme: 'Submit to governing authorities; love fulfills the whole law; the night is far gone — put on Christ.',
    },
    {
      number: 14, verseCount: 23,
      title: 'Strong and Weak: Receive One Another',
      theme: 'Do not despise or judge the weak in faith; pursue peace and mutual edification; the kingdom is righteousness, peace, and joy.',
    },
    {
      number: 15, verseCount: 33,
      title: 'Christ Our Example; Paul\'s Mission Plans',
      theme: 'Bear with the weak as Christ bore with us; Paul\'s priestly mission to the Gentiles reaches Rome and beyond.',
    },
    {
      number: 16, verseCount: 27,
      title: 'Greetings and Final Doxology',
      theme: 'Greetings to co-workers; beware divisive teachers; to the only wise God, through Jesus Christ, be glory forever.',
      keyVerse: 25,
    },
  ],

  arcs: [
    // ── Primary argument spine ────────────────────────────────────────────────
    {
      id: 'arc-thesis',
      relationship: 'grounds',
      label: 'Thesis: righteousness by faith (1:16–17)',
      fromChapter: 1, fromVerse: 16,
      toChapter: 3,  toVerse: 21,
      accent: 'primary',
    },
    {
      id: 'arc-abraham',
      relationship: 'grounds',
      label: 'OT proof: Abraham justified by faith',
      fromChapter: 3, fromVerse: 21,
      toChapter: 4,  toVerse: 3,
      accent: 'primary',
    },
    {
      id: 'arc-peace',
      relationship: 'inference',
      label: 'Therefore: justified → peace with God',
      fromChapter: 4, fromVerse: 25,
      toChapter: 5,  toVerse: 1,
      accent: 'primary',
    },
    {
      id: 'arc-no-condemnation',
      relationship: 'progression',
      label: 'Climax: no condemnation in Christ',
      fromChapter: 5, fromVerse: 1,
      toChapter: 8,  toVerse: 1,
      accent: 'primary',
    },
    {
      id: 'arc-doxology',
      relationship: 'result',
      label: 'Election → doxology (11:33–36)',
      fromChapter: 9, fromVerse: 6,
      toChapter: 11, toVerse: 33,
      accent: 'primary',
    },
    {
      id: 'arc-therefore-bodies',
      relationship: 'inference',
      label: 'Therefore: present your bodies',
      fromChapter: 11, fromVerse: 36,
      toChapter: 12,  toVerse: 1,
      accent: 'primary',
    },

    // ── Secondary arcs ────────────────────────────────────────────────────────
    {
      id: 'arc-wrath-to-guilt',
      relationship: 'progression',
      label: 'All — Jew and Greek — under sin',
      fromChapter: 1, fromVerse: 18,
      toChapter: 3,  toVerse: 9,
      accent: 'secondary',
    },
    {
      id: 'arc-adam-christ',
      relationship: 'contrast',
      label: 'Adam brought death; Christ brings life',
      fromChapter: 5, fromVerse: 12,
      toChapter: 5,  toVerse: 17,
      accent: 'secondary',
    },
    {
      id: 'arc-remnant',
      relationship: 'explanation',
      label: 'Remnant by grace, not works',
      fromChapter: 9, fromVerse: 27,
      toChapter: 11, toVerse: 5,
      accent: 'secondary',
    },
    {
      id: 'arc-strong-weak',
      relationship: 'series',
      label: 'Love applied: receive the weak',
      fromChapter: 12, fromVerse: 1,
      toChapter: 14,  toVerse: 1,
      accent: 'secondary',
    },
  ],
}
